/**
 * GMA-1608 (#330 AC-F1/F2/F4) — pure announcement model for the alerts rail
 * live regions.
 *
 * The command center's job is surfacing *changing* safety state; a new alert
 * that is only a visual badge change is invisible to a screen-reader user —
 * on a care console an un-announced safety alert fails the same way a dropped
 * cascade does, on the perception side. This module computes WHAT to announce
 * when the alerts list changes between renders; AlertsRail owns the live
 * regions that speak it.
 *
 * Contract (UI-free, fail-safe like overviewModel/alertsQueueModel):
 *   buildAlertAnnouncement(prevAlerts, nextAlerts) -> { text, level } | null
 *   - level "assertive" when the arrivals include a critical/safety alert
 *     (interrupts, AC-F1); "polite" for routine arrivals;
 *   - text carries content + count ("New critical alert: Bob Fall — 3 unread",
 *     AC-F2), coalescing a burst into ONE announcement (AC-F4);
 *   - null when nothing new arrived, or on null/undefined/malformed input
 *     (never throws — one bad payload can't break the rail render).
 *
 * Severity and lifecycle vocabulary are REUSED from the shared models
 * (severityUrgency / ALERT_STATES), never re-declared, so "critical" and
 * "unread" here can't drift from what the queue itself means by them.
 */
import { severityUrgency } from "../../pages/web/overviewModel";
import { alertState, ALERT_STATES } from "../../pages/web/alertsQueueModel";

// The interrupting class: alerts at critical urgency or above. Derived from the
// shared urgency ranking (not a new severity string) so a vocabulary change in
// the design tokens propagates here automatically.
const CRITICAL_URGENCY = severityUrgency("critical");

// "Unread" = not yet acknowledged: the first lifecycle state ("new"). Alerts
// with no state field default to "new" via alertState, matching the queue.
const UNREAD_STATE = ALERT_STATES[0];

// Standard clip-rect visually-hidden style: present in the accessibility tree
// (so live regions announce) but invisible. display:none / visibility:hidden
// would silence the region. Shared by AlertsRail's two live-region containers.
export const VISUALLY_HIDDEN_STYLE = Object.freeze({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});

/** Valid rows only (drop null/primitive junk); null when not an array. */
function validRows(list) {
  return Array.isArray(list)
    ? list.filter((row) => row !== null && typeof row === "object")
    : null;
}

/**
 * Stable identity for diffing between renders: the row id when present,
 * otherwise a content key (same fields the rail's own render key uses), so
 * re-renders of identical id-less data stay silent.
 */
function alertKey(alert) {
  if (alert.id !== undefined && alert.id !== null) return `id:${alert.id}`;
  return `k:${alert.severity}|${alert.type}|${alert.clientName}|${alert.createdAt}`;
}

function isCritical(alert) {
  return severityUrgency(alert.severity) >= CRITICAL_URGENCY;
}

/**
 * Compute the single (coalesced) live-region announcement for an alerts-list
 * change, or null when there is nothing to say.
 *
 * @param {*} prevAlerts alerts as of the previous render
 * @param {*} nextAlerts alerts as of the current render
 * @returns {{ text: string, level: "assertive"|"polite" }|null}
 */
export function buildAlertAnnouncement(prevAlerts, nextAlerts) {
  const prev = validRows(prevAlerts);
  const next = validRows(nextAlerts);
  if (prev === null || next === null) return null;

  const prevKeys = new Set(prev.map(alertKey));
  const arrived = next.filter((alert) => !prevKeys.has(alertKey(alert)));
  if (arrived.length === 0) return null;

  const unread = next.filter((alert) => alertState(alert) === UNREAD_STATE).length;
  const suffix = ` — ${unread} unread`;
  const critical = arrived.filter(isCritical);

  if (critical.length > 0) {
    if (arrived.length === 1) {
      const alert = critical[0];
      // Same fallbacks the rail renders, so AT hears exactly what is shown.
      const clientName = alert.clientName || "Unknown client";
      const label = alert.type || "Alert";
      return {
        level: "assertive",
        text: `New critical alert: ${clientName} ${label}${suffix}`,
      };
    }
    return {
      level: "assertive",
      text: `${arrived.length} new alerts (${critical.length} critical)${suffix}`,
    };
  }

  if (arrived.length === 1) {
    const alert = arrived[0];
    const clientName = alert.clientName || "Unknown client";
    const label = alert.type || "Alert";
    return { level: "polite", text: `New alert: ${clientName} ${label}${suffix}` };
  }
  return { level: "polite", text: `${arrived.length} new alerts${suffix}` };
}
